import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, Clock, Calendar, AlertCircle, ArrowLeft, Send, MessageSquare, X, Users, Filter
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const EmployeeTasksNew = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/employee/my-tasks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setTasks(data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchTaskDetails = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${taskId}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSelectedTask(data);
      setShowTaskModal(true);
      
      // Mark task notifications as read
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/mark-task-read/${taskId}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        // Trigger notification refresh
        window.dispatchEvent(new Event('refreshNotifications'));
      } catch (notifError) {
        console.error('Failed to mark notifications as read:', notifError);
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      toast.error('Failed to load task details');
    }
  };

  const markTaskComplete = async () => {
    if (!selectedTask) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${selectedTask.task.id}/mark-complete`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        toast.success('Task marked as complete');
        setShowTaskModal(false);
        setSelectedTask(null);
        fetchTasks();
        // Refresh notifications to update task count
        window.dispatchEvent(new Event('refreshNotifications'));
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to mark task as complete');
      }
    } catch (error) {
      console.error('Error marking task complete:', error);
      toast.error('Failed to complete task');
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks/${selectedTask.task.id}/reply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ message: replyMessage })
        }
      );

      if (response.ok) {
        toast.success('Reply sent');
        setReplyMessage('');
        fetchTaskDetails(selectedTask.task.id);
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const filteredTasks = tasks
    .filter(task => {
      if (activeTab === 'active') return task.status === 'active';
      return task.status === 'completed';
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        const dateA = new Date(a.start_date || a.created_at);
        const dateB = new Date(b.start_date || b.created_at);
        return dateB - dateA;
      }
      return 0;
    });

  const priorityColors = {
    high: { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-500' },
    medium: { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-500' },
    low: { bg: 'bg-blue-50', border: 'border-blue-500', text: 'text-blue-700', badge: 'bg-blue-500' }
  };

  return (
    <section className="space-y-6">
      {/* Header */}
      <div className="w-full bg-white rounded-3xl shadow-lg p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          <Button onClick={() => navigate('/profile')} variant="ghost" size="sm" className="self-start">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">My Tasks</h1>
            <p className="text-sm text-gray-500 mt-1">View and manage your assigned tasks</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="w-full bg-white rounded-3xl shadow-lg overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('active')}
            className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-sm font-medium transition-colors ${
              activeTab === 'active'
                ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Active Tasks ({tasks.filter(t => t.status === 'active').length})
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-sm font-medium transition-colors ${
              activeTab === 'completed'
                ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            Completed Tasks ({tasks.filter(t => t.status === 'completed').length})
          </button>
        </div>

        {/* Sorting */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex gap-2 items-center justify-end">
            <span className="text-sm text-gray-600">Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            >
              <option value="date">Date</option>
            </select>
          </div>
        </div>

        {/* Task List */}
        <div className="p-4 sm:p-6">
          {filteredTasks.length > 0 ? (
            <div className="space-y-4">
              {filteredTasks.map(task => {
                const style = priorityColors[task.priority];
                return (
                  <div
                    key={task.id}
                    className={`w-full ${style.bg} border-l-4 ${style.border} rounded-3xl shadow-lg p-3 sm:p-4 cursor-pointer hover:shadow-xl transition-shadow`}
                    onClick={() => fetchTaskDetails(task.id)}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900">{task.title}</h3>
                          <span className={`${style.badge} text-white text-xs px-2 py-1 rounded-full whitespace-nowrap`}>
                            {task.priority}
                          </span>
                          {task.status === 'completed' && (
                            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full whitespace-nowrap flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Completed
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-700">{task.description}</p>
                      
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                        {task.created_by_name && (
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            From: {task.created_by_name}
                          </span>
                        )}
                        {task.start_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Start: {new Date(task.start_date).toLocaleDateString()}
                          </span>
                        )}
                        {task.end_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Due: {new Date(task.end_date).toLocaleDateString()}
                          </span>
                        )}
                        {task.status === 'completed' && task.completed_at && (
                          <span className="flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Completed: {new Date(task.completed_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex gap-2 mt-2">
                        <Button 
                          size="sm" 
                          className="bg-teal-600 hover:bg-teal-700 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            fetchTaskDetails(task.id);
                          }}
                        >
                          <MessageSquare className="w-4 h-4 mr-1" />
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No {activeTab} tasks</p>
            </div>
          )}
        </div>
      </div>

      {/* Task Detail Modal */}
      {showTaskModal && selectedTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{selectedTask.task.title}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedTask.task.status === 'completed' ? 'Completed Task' : 'Active Task'}
                </p>
              </div>
              <button
                onClick={() => { setShowTaskModal(false); setSelectedTask(null); }}
                className="text-gray-400 hover:text-gray-600 ml-4"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              {/* Task Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-gray-700 mb-3">{selectedTask.task.description}</p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                  <span className={`px-3 py-1 rounded ${priorityColors[selectedTask.task.priority].badge} text-white`}>
                    {selectedTask.task.priority} priority
                  </span>
                  {selectedTask.task.created_by_name && (
                    <span>Created by: {selectedTask.task.created_by_name}</span>
                  )}
                  {selectedTask.task.start_date && (
                    <span>Start: {new Date(selectedTask.task.start_date).toLocaleDateString()}</span>
                  )}
                  {selectedTask.task.end_date && (
                    <span>Due: {new Date(selectedTask.task.end_date).toLocaleDateString()}</span>
                  )}
                </div>
              </div>

              {/* Replies Section */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                <MessageSquare className="w-5 h-5 inline mr-2" />
                Conversation
              </h3>
              
              <div className="space-y-3 mb-6">
                {selectedTask.replies && selectedTask.replies.length > 0 ? (
                  selectedTask.replies.map(reply => (
                    <div key={reply.id} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <span className="font-medium text-gray-900">{reply.user_name}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(reply.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700">{reply.message}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">No replies yet</p>
                )}
              </div>

              {/* Reply Input (only for active tasks) */}
              {selectedTask.task.status === 'active' && (
                <>
                  <div className="border-t pt-4 mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Add Reply</label>
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      placeholder="Type your reply..."
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent mb-3"
                    />
                    <Button onClick={sendReply} className="bg-teal-600 hover:bg-teal-700 text-white w-full sm:w-auto">
                      <Send className="w-4 h-4 mr-2" />
                      Send Reply
                    </Button>
                  </div>

                  {/* Mark Complete Button */}
                  <div className="border-t pt-4">
                    <Button 
                      onClick={markTaskComplete} 
                      className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Complete
                    </Button>
                  </div>
                </>
              )}

              {selectedTask.task.status === 'completed' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">This task has been completed</p>
                  {selectedTask.task.completed_at && (
                    <p className="text-sm text-green-600 mt-1">
                      Completed on {new Date(selectedTask.task.completed_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default EmployeeTasksNew;
